import { Box, IconButton, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  barragemLayers,
  googleSatelite,
  HidroLayer,
  HidroLayerGroup,
  HidroLayersComponent,
  HidroMap,
} from "../../components";
import { PolyZas, PolyZss } from "../../components/zas";

import Logo from "../../assets/logoSemFundo.png";
import { BarragemData, useBarragemContext } from "../../context";
import { Theme, useTheme } from "@mui/material";
import { RequestError } from "../../services";
import { ErrorOnDialog } from "../../components/modals";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { Close, List } from "@mui/icons-material";
import { generateRandomHash } from "../../core";

export const SectionV = () => {
  const [error, setError] = useState<RequestError>();
  const [showSumario, setShowSumario] = useState(true);

  const layers = useRef(new HidroLayerGroup({
    name: "Layers",
    showing: true,
    layers: [],
  }));

  const { barragem } = useBarragemContext();
  const theme = useTheme();

  useEffect(() => {
    if (!barragem) return;
    if ("error" in barragem) {
      setError(barragem);
      return;
    }

    getLayers(barragem, theme, layers.current);
  }, [barragem, theme]);

  return (
    <>
      <ErrorOnDialog
        open={Boolean(error)}
        error={error?.error ?? ""}
        message={error?.message ?? ""}
        onClose={() => setError(undefined)}
      />

      <Box sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "row",
        position: 'relative',
      }}>
        {!showSumario && (
          <div
            onClick={() => setShowSumario(true)}
            style={{
              width: "46px",
              height: "46px",
              padding: "5px",
              display: "flex",
              position: 'absolute',
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              border: "1px solid black",
              borderTopRightRadius: "50%",
              borderBottomRightRadius: "50%",
              boxShadow: "10px",
              top: 5,
              left: 0,
              zIndex: 10000,
            }}
          >
            <List />
          </div>
        )}

        {showSumario && <Box sx={{
          p: "0.5em",
          gap: "0.5em",
          width: "250px",
          display: "flex",
          paddingX: "0.5em",
          paddingY: "0.3em",
          position: 'relative',
          flexDirection: "column",
        }}>

          {/* Botão flutuante */}
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: "0.5em",
              right: "0.5em",
              zIndex: 1000,
            }}
            onClick={() => setShowSumario(!showSumario)}
          >
            <Close fontSize="small" />
          </IconButton>

          <SimpleTreeView>
            <TreeItem
              key={'1'}
              itemId={"1"}
              label={
                <Typography fontWeight={'bold'}>
                  Cadastro na ZAS
                </Typography>
              }
            />
          </SimpleTreeView>

          <HidroLayersComponent hidroLayers={layers.current} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <img
              src={Logo}
              alt={"logo"}
              style={{
                objectFit: "contain",
                height: `7em`,
                width: `7em`,
              }}
            />
          </div>
        </Box>}

        <Box sx={{
          flex: 80,
          p: "0.5em",
          width: "100%",
          height: "100%",
          display: "flex",
        }}>
          <HidroMap
            key={generateRandomHash(8)}
            recenter
            expandable
            screenshot
            startWithTile
            chooseMap
            animateStart={false}
            hidroLayers={layers.current}
          />
        </Box>
      </Box>

    </>
  );
};

const getLayers = (barragemData: BarragemData, theme: Theme, layers: HidroLayerGroup) => {
  layers.clearLayers();
  layers.push(barragemLayers(barragemData, theme, false));

  const zas = barragemData.zas;
  if (zas && !("error" in zas)) {

    if (zas.polygon && zas.polygon instanceof PolyZas) {
      layers.push(new HidroLayer({
        name: "ZAS",
        showing: true,
        layer: zas.polygon,
      }));
    }

    if (zas.polygonZss && zas.polygonZss instanceof PolyZss) {
      layers.push(new HidroLayer({
        name: "ZSS",
        showing: true,
        layer: zas.polygonZss,
      }));
    }

    if (zas.rotasFuga && !('error' in zas.rotasFuga)) {
      const layerRotas = new HidroLayerGroup({ name: "Rotas de Fuga", showing: false, layers: [] });

      zas.rotasFuga.forEach(rota => {
        layerRotas.push(new HidroLayer({
          name: `R${rota.properties.nome}`,
          showing: true,
          layer: rota,
          style: theme.palette.geometries.rotaFuga,
        }));
      });
      layers.push(layerRotas);
    }

    if (zas?.pontosEncontro && !('error' in zas.pontosEncontro)) {
      layers.push(new HidroLayerGroup({
        name: "Pontos de Encontro",
        showing: false,
        layers: zas.pontosEncontro.map(layer => new HidroLayer({
          name: layer.properties.ponto,
          showing: true,
          layer: layer,
        })),
      }));
    }

    if (zas?.uniSensivel && !('error' in zas.uniSensivel)) {
      layers.push(new HidroLayerGroup({
        name: "Uni. Sensíveis",
        showing: false,
        layers: zas.uniSensivel.map(layer => new HidroLayer({
          name: layer.properties.nome,
          showing: true,
          layer: layer,
          style: layer.properties.style,
        })),
      }));
    }

    if (zas.cadastros && !('error' in zas.cadastros)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cadastrosPorTipo: { [key: string]: any[]; } = {};
      for (const cadastro of zas.cadastros) {
        const tipo = cadastro.properties.tipo || 'Outros';
        if (!cadastrosPorTipo[tipo]) {
          cadastrosPorTipo[tipo] = [];
        }
        cadastrosPorTipo[tipo].push(cadastro);
      }

      const subGrupos = Object.entries(cadastrosPorTipo).map(([tipo, cadastrosDoTipo]) => {
        return new HidroLayerGroup({
          name: tipo,
          showing: tipo === 'Fauna',
          layers: cadastrosDoTipo.map((cadastro) => new HidroLayer({
            name: `Cadastro ${cadastro.properties.id}`,
            showing: true,
            layer: cadastro,
          })),
        });
      });

      layers.push(new HidroLayerGroup({
        name: "Cadastro ZAS",
        showing: true,
        layers: subGrupos,
      }));
    }

    if (zas?.sirenes && !('error' in zas.sirenes)) {
      layers.push(new HidroLayerGroup({
        name: "Sirenes",
        showing: false,
        layers: zas.sirenes.map((layer, i) => {
          const layersAdd = [
            new HidroLayer({
              name: "Sirene",
              showing: true,
              layer: layer,
              style: theme.palette.geometries.sirenes,
            })
          ];
          if (layer.properties.areaCobertura) {
            layersAdd.push(new HidroLayer({
              name: "Cobertura",
              showing: true,
              layer: layer.properties.areaCobertura,
            }));
          }

          return new HidroLayerGroup({
            name: `Sirene ${i + 1}`,
            showing: false,
            layers: layersAdd,
          });
        }),
      }));
    }

    if (zas?.rotasAlternativas && !('error' in zas.rotasAlternativas)) {
      layers.push(new HidroLayerGroup({
        name: "Rotas Alternativas",
        showing: false,
        layers: zas.rotasAlternativas.map(layer => new HidroLayer({
          name: layer.properties.nome,
          showing: false,
          layer: layer,
        })),
      }));
    }
  }

  layers.push(new HidroLayer({
    name: "Google Satelite",
    showing: true,
    layer: googleSatelite(),
  }));

  return layers;
};